import React, { useEffect, useState } from 'react';
import {
    AppliedFilter,
    OutgoingFilter,
    SortOption,
    Project,
    UserDetails,
    Label,
    PackCopy,
} from '../types';
import { ProjectLibraryContext } from '../contexts/ProjectLibraryContext';
import { useInterval } from '../hooks/useInterval';
import { useAuth } from '@clerk/clerk-react';
import {
    deleteLabel,
    deletePackCopy,
    duplicatePackCopy,
    fetchProjectDetails,
    fetchProjectLibraryData,
    updatePackCopyName,
    updatePackCopyNotes,
} from '../utils/backendServices';
import { ProjectDetailsContext } from '../contexts/ProjectDetailsContext';
import { useParams } from 'react-router-dom';
import {
    NavigateWithinOrg,
    useNavigateWithinOrg,
    useNavigateWithinProject,
} from '../utils/navigation';

interface ProjectDetailsContextProviderProps {
    children: React.ReactNode;
}
const ProjectDetailsContextProvider: React.FC<
    ProjectDetailsContextProviderProps
> = ({ children }) => {
    const [project, setProject] = useState<Project | null>(null);
    const [labels, setLabels] = useState<Label[]>([]);
    const [labelThumbnailUrls, setLabelThumbnailUrls] = useState<
        Record<string, string>
    >({});
    const [packCopies, setPackCopies] = useState<PackCopy[]>([]);
    const [loadingState, setLoadingState] = useState<
        'loading' | 'loaded' | 'error'
    >('loaded');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const { orgId } = useAuth();
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigateWithinProject();
    const [userDetailsMapping, setUserDetailsMapping] = useState<{
        [user_id: string]: UserDetails | null;
    }>({});

    if (!projectId) {
        return <NavigateWithinOrg to="/projects" />;
    }
    const state = {
        project,
        labels,
        labelThumbnailUrls,
        packCopies,
        loadingState,
        userDetailsMapping,
        errorMessage,
    };

    const handleProjectDetailsRequest = async (showSpinner = true) => {
        try {
            if (showSpinner) {
                setLoadingState('loading');
            }
            const response = await fetchProjectDetails(projectId);
            setProject(response.project);
            setLabels(response.labels);
            setLabelThumbnailUrls(response.label_thumbnail_urls);
            setPackCopies(response.pack_copies);
            setUserDetailsMapping(response.user_details_mapping);
            setErrorMessage(null);
            setLoadingState('loaded');
        } catch (error) {
            console.error(error);
            setLoadingState('error');
            setErrorMessage('Failed to fetch project details');
        }
    };

    useEffect(() => {
        // if (organization) {
        handleProjectDetailsRequest();
        //
    }, [orgId]);

    useInterval(() => {
        handleProjectDetailsRequest(false).catch((error) => {
            console.log(error);
        });
    }, 60000);

    const refresh = (showSpinner = true) => {
        return handleProjectDetailsRequest(showSpinner);
    };

    const handlers = {
        handlePackCopyDuplicate: async (packCopyId: string) => {
            setLoadingState('loading');
            navigate('/pack-copies');
            const response = await duplicatePackCopy(packCopyId);
            navigate(`pack-copies/${response.pack_copy_detailed.id}`);
            setLoadingState('loaded');
            refresh(false);
        },
        handlePackCopyDelete: async (packCopyId: string) => {
            setLoadingState('loading');
            navigate('/pack-copies');
            await deletePackCopy(packCopyId);
            await refresh(true);
            setLoadingState('loaded');
        },
        handleLabelDelete: async (labelId: string) => {
            setLoadingState('loading');
            navigate('/labels');
            await deleteLabel(labelId);
            await refresh(true);
            setLoadingState('loaded');
        },
    };

    return (
        <ProjectDetailsContext.Provider value={{ state, refresh, handlers }}>
            {children}
        </ProjectDetailsContext.Provider>
    );
};

export default ProjectDetailsContextProvider;
